// ** Component Imports
import { Button, Chip, Heading, Typography } from '@/shared/ui'

// ** Icon Imports
import { CheckContained } from '@/shared/ui/icons'

// ** Entity Imports
import { CustomPlanDetails, type MyPlan } from '@/entities/custom-pricing'

// ** Utility Imports
import { formatPlanAmount, getIntervalLabel, getPlanFeatures } from '@/lib/plan'
import { cn } from '@/lib/utils'

// Same chrome as the legacy subscription `CardWrapper`.
const CARD_CLASSES =
	'flex w-full flex-col gap-4 rounded-lg bg-primary-bg-20 p-4 shadow-field'

interface PlanCardProps {
	plan: MyPlan
	isRedirecting: boolean
	onSubscribe: () => void
	onManageBilling: () => void
}

const getBadgeLabel = (plan: MyPlan): string | null => {
	if (plan.isCurrent) return 'Current plan'
	if (plan.planKey === 'yearly') return 'Best offer'
	if (plan.isCustom) return 'Negotiated'

	return null
}

/**
 * The signed-in user's plan, rendered entirely from `GET /subscriptions/my-plan`:
 * either their live subscription (manage it) or the plan offered at checkout.
 */
export const PlanCard = ({
	plan,
	isRedirecting,
	onSubscribe,
	onManageBilling
}: PlanCardProps) => {
	// ** Variables
	const badgeLabel = getBadgeLabel(plan)
	const features = getPlanFeatures(plan)
	const intervalLabel = getIntervalLabel(plan.interval)
	const formattedAmount =
		plan.amount !== null ? formatPlanAmount(plan.amount, plan.currency) : null
	// legacy monthly is only ever kept, never sold; and without a Stripe price
	// there is nothing to check out against
	const canSubscribe = plan.planKey !== 'legacy_monthly' && plan.price !== null

	return (
		<div className={CARD_CLASSES}>
			<div className='flex items-center gap-4'>
				{/* a custom plan's name is the admin's Stripe nickname, not customer copy */}
				<Heading variant='h4' weight='semi-bold'>
					{plan.isCustom
						? 'YOUR PLAN'
						: (plan.name ?? 'Professional').toUpperCase()}
				</Heading>

				{badgeLabel !== null && (
					<span className='shrink-0'>
						<Chip label={badgeLabel} color='primary' />
					</span>
				)}
			</div>

			{/* Negotiated terms can be tiered, so they get the same breakdown as the
			    billing tab instead of a single headline price */}
			{plan.isCustom && (
				<>
					<Typography>
						{plan.isCurrent
							? 'You’re on a negotiated plan agreed for your organization.'
							: 'These are the terms agreed for your organization.'}
					</Typography>

					<CustomPlanDetails plan={plan} />
				</>
			)}

			{!plan.isCustom && formattedAmount !== null && (
				<div className='mb-4'>
					<span className='flex items-baseline gap-2'>
						<Heading variant='h2' weight='bold'>
							{formattedAmount}
						</Heading>

						{intervalLabel !== null && (
							<Typography weight='regular'>{intervalLabel}</Typography>
						)}
					</span>

					{/* checkout charges the full amount up front — say so before redirecting */}
					{!plan.isCurrent && canSubscribe && (
						<Typography weight='regular'>
							{`You'll be charged ${formattedAmount} today.`}
						</Typography>
					)}
				</div>
			)}

			{features.map((feature, index) => (
				<span key={`${index}-${feature}`} className='flex items-center gap-2'>
					<CheckContained />

					<Typography weight='regular'>{feature}</Typography>
				</span>
			))}

			{/* pinned to the bottom so it lines up with the enterprise card's button */}
			<div className='mt-auto pt-6'>
				{plan.isCurrent ? (
					<Button
						label='Manage billing'
						variant='primary'
						fullWidth
						disabled={isRedirecting}
						isLoading={isRedirecting}
						onClick={onManageBilling}
					/>
				) : canSubscribe ? (
					<Button
						label='Subscribe'
						variant='primary'
						fullWidth
						disabled={isRedirecting}
						isLoading={isRedirecting}
						onClick={onSubscribe}
					/>
				) : (
					<Typography variant='body2'>
						Contact support to complete your subscription.
					</Typography>
				)}

				{/* the portal is narrowed for negotiated plans, so terms change via support */}
				{plan.isCustom && plan.isCurrent && (
					<div className='mt-2'>
						<Typography variant='caption'>
							Use the billing portal for invoices and payment details. To change
							your negotiated terms, please contact support.
						</Typography>
					</div>
				)}
			</div>
		</div>
	)
}

export const PlanCardSkeleton = () => (
	<div className={cn(CARD_CLASSES, 'animate-pulse')} aria-busy='true'>
		<div className='h-7 w-40 rounded bg-primary-bg-50' />
		<div className='mb-4 h-10 w-32 rounded bg-primary-bg-50' />
		{[0, 1, 2, 3].map((row) => (
			<div key={row} className='h-5 w-3/4 rounded bg-primary-bg-50' />
		))}
		<div className='mt-6 h-10 w-36 rounded bg-primary-bg-50' />
	</div>
)

export const PlanCardError = ({
	isRetrying,
	onRetry
}: {
	isRetrying: boolean
	onRetry: () => void
}) => (
	<div className={CARD_CLASSES} role='alert'>
		<Typography weight='regular'>
			We couldn&apos;t load your plan. Please try again.
		</Typography>

		<div>
			<Button
				label='Retry'
				variant='secondary'
				disabled={isRetrying}
				isLoading={isRetrying}
				onClick={onRetry}
			/>
		</div>
	</div>
)
